import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Search, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function MetaAdsAccountSelector({ oauthConnected, onAccountSelected }) {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showList, setShowList] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load accounts when OAuth is connected
  useEffect(() => {
    console.log('[MetaAdsAccountSelector] useEffect', { oauthConnected, selectedAccount });
    if (oauthConnected && !selectedAccount) {
      console.log('[MetaAdsAccountSelector] Loading accounts...');
      loadAccounts();
    }
  }, [oauthConnected]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/marketing/meta-ads/accounts');
      setAccounts(res.data);
      if (res.data.length === 0) {
        toast.error('Nenhuma conta de anúncios encontrada');
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      toast.error('Erro ao carregar contas de anúncios');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAccounts = () => {
    if (!searchQuery) return accounts;
    const query = searchQuery.toLowerCase();
    return accounts.filter(
      a =>
        a.name.toLowerCase().includes(query) ||
        a.id.toLowerCase().includes(query)
    );
  };

  const handleSelectAccount = async (account) => {
    setSaving(true);
    try {
      await authAxios.post('/marketing/meta-ads/select-account', {
        ad_account_id: account.id,
        ad_account_name: account.name,
        currency: account.currency || 'EUR',
      });
      setSelectedAccount(account);
      setShowList(false);
      toast.success('Conta selecionada com sucesso');
      if (onAccountSelected) {
        onAccountSelected({
          id: account.id,
          name: account.name,
          currency: account.currency || 'EUR',
        });
      }
    } catch (err) {
      console.error('Failed to select account:', err);
      toast.error('Erro ao selecionar conta');
    } finally {
      setSaving(false);
    }
  };

  if (!oauthConnected) {
    return null;
  }

  const filteredAccounts = getFilteredAccounts();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div>
          <CardTitle className="text-base">Conta de Anúncios</CardTitle>
          <CardDescription className="text-xs">
            Selecione a conta para visualizar campanhas
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {selectedAccount ? (
          <>
            {/* Selected account display */}
            <div className="p-3 rounded-md bg-green-50 border border-green-200">
              <p className="text-xs text-green-600 font-semibold mb-1">Conta selecionada</p>
              <p className="text-sm font-medium text-green-900">{selectedAccount.name}</p>
              <p className="text-xs text-green-700 font-mono">{selectedAccount.id}</p>
            </div>

            {/* Change account button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowList(!showList);
                setSearchQuery('');
              }}
              className="w-full"
            >
              Trocar conta
            </Button>
          </>
        ) : (
          <>
            {/* Search box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowList(true)}
                className="pl-10"
              />
            </div>

            {/* Accounts list dropdown */}
            {showList && (
              <div className="border rounded-md bg-white max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Carregando contas...
                  </div>
                ) : filteredAccounts.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {accounts.length === 0
                      ? 'Nenhuma conta encontrada'
                      : 'Nenhuma conta corresponde à busca'}
                  </div>
                ) : (
                  filteredAccounts.map(account => (
                    <button
                      key={account.id}
                      onClick={() => handleSelectAccount(account)}
                      disabled={saving}
                      className="w-full text-left p-3 border-b hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{account.name}</p>
                          <p className="text-xs text-muted-foreground font-mono truncate">{account.id}</p>
                        </div>
                        <Badge
                          variant={account.account_status === 1 ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {account.account_status === 1 ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Helper text */}
            {!showList && accounts.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {accounts.length} contas disponíveis. Clique no campo de busca para selecionar.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
